// Calculs dérivés du groupe "Mes mesures" (Profil, Lot 2a) — jamais stockés en base,
// toujours recalculés depuis les mesures brutes du relevé consulté.

export type Gender = "male" | "female"

export const WAIST_TO_HEIGHT_HEALTHY_THRESHOLD = 0.5

// Garde stricte de présence : rejette null/undefined, mais aussi tout ce qui n'est pas
// un véritable nombre positif fini (ex. une chaîne numérique "0"/"70" qui aurait échappé
// à la normalisation côté supabase.ts — voir parseBodyMeasurementsRow). Un simple `!x`
// laisse passer une string non vide ("0" est truthy en JS) : ne pas revenir à ce pattern.
export function isPresentMeasure(x: unknown): x is number {
  return typeof x === "number" && Number.isFinite(x) && x > 0
}

export function computeWaistToHeightRatio(
  waistCm: number | null | undefined,
  heightCm: number | null | undefined
): number | null {
  if (!isPresentMeasure(waistCm) || !isPresentMeasure(heightCm)) return null
  return waistCm / heightCm
}

/**
 * Masse grasse estimée — méthode US Navy (Hodgdon & Beckett).
 * Coefficients repris tels quels du brief produit, ne pas reconstruire de mémoire.
 * Renvoie null si une mesure nécessaire manque pour le genre concerné (cou + taille
 * chez l'homme, cou + taille + hanches chez la femme) — jamais un calcul partiel.
 */
export function computeBodyFatPercentUSNavy(params: {
  gender: Gender
  neckCm: number | null | undefined
  waistCm: number | null | undefined
  heightCm: number | null | undefined
  hipsCm?: number | null
}): number | null {
  const { gender, neckCm, waistCm, heightCm, hipsCm } = params
  if (!isPresentMeasure(neckCm) || !isPresentMeasure(waistCm) || !isPresentMeasure(heightCm)) return null

  if (gender === "male") {
    const waistMinusNeck = waistCm - neckCm
    if (waistMinusNeck <= 0) return null
    const denom = 1.0324 - 0.19077 * Math.log10(waistMinusNeck) + 0.15456 * Math.log10(heightCm)
    if (denom <= 0) return null
    return 495 / denom - 450
  }

  if (!isPresentMeasure(hipsCm)) return null
  const waistPlusHipsMinusNeck = waistCm + hipsCm - neckCm
  if (waistPlusHipsMinusNeck <= 0) return null
  const denom = 1.29579 - 0.35004 * Math.log10(waistPlusHipsMinusNeck) + 0.221 * Math.log10(heightCm)
  if (denom <= 0) return null
  return 495 / denom - 450
}

/** Masse maigre = poids × (1 − %MG / 100). Null si le %MG n'est pas disponible. */
export function computeLeanMassKg(
  weightKg: number | null | undefined,
  bodyFatPct: number | null | undefined
): number | null {
  if (!isPresentMeasure(weightKg) || !isPresentMeasure(bodyFatPct)) return null
  return weightKg * (1 - bodyFatPct / 100)
}
